import React, { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import Rating, { IconContainerProps } from "@mui/material/Rating";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  Typography,
  TextField,
} from "@mui/material";
import { LoadingButton as Button } from "@mui/lab";
import { InputGroup, InputLabel } from "@/components/Form";
import SentimentVeryDissatisfiedIcon from "@mui/icons-material/SentimentVeryDissatisfied";
import SentimentDissatisfiedIcon from "@mui/icons-material/SentimentDissatisfied";
import SentimentSatisfiedIcon from "@mui/icons-material/SentimentSatisfied";
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAltOutlined";
import SentimentVerySatisfiedIcon from "@mui/icons-material/SentimentVerySatisfied";
import { IOrder } from "@/interfaces";
import { enqueueSnackbar } from "notistack";
import { OrderStore } from "@/store/orders";
import { inject, observer } from "mobx-react";
import Store from "@/store";
import { IOrderFeedback } from "@/interfaces/post/order";
import { UserStore } from "@/store/user";

const StyledRating = styled(Rating)(({ theme }) => ({
  "& .MuiRating-iconEmpty .MuiSvgIcon-root": {
    color: theme.palette.action.disabled,
  },
}));

const customIcons: {
  [index: string]: {
    icon: React.ReactElement;
    label: string;
  };
} = {
  1: {
    icon: <SentimentVeryDissatisfiedIcon color="error" />,
    label: "Very Dissatisfied",
  },
  2: {
    icon: <SentimentDissatisfiedIcon color="error" />,
    label: "Dissatisfied",
  },
  3: {
    icon: <SentimentSatisfiedIcon color="warning" />,
    label: "Neutral",
  },
  4: {
    icon: <SentimentSatisfiedAltIcon color="success" />,
    label: "Satisfied",
  },
  5: {
    icon: <SentimentVerySatisfiedIcon color="success" />,
    label: "Very Satisfied",
  },
};

function IconContainer(props: IconContainerProps) {
  const { value, ...other } = props;
  return (
    <span {...other} style={{ width: "32px" }}>
      {customIcons[value].icon}
    </span>
  );
}

interface ReviewProp {
  userStore?: UserStore;
  orderStore?: OrderStore;
  openDialog: boolean;
  setOpenDialog: (value: boolean) => void;
  selectedOrder: IOrder | null;
}
const Review = inject(
  Store.type.orderStore,
  Store.type.userStore,
)(
  observer(
    ({
      orderStore,
      userStore,
      openDialog,
      setOpenDialog,
      selectedOrder,
    }: ReviewProp) => {
      const theme = useTheme();

      const [isSubmiting, setIsSubmiting] = useState(false);

      const [rating, setRating] = React.useState<number | null>(null);
      const [error, setError] = React.useState<string>("");
      const [comment, setCommnet] = React.useState<string>("");

      const handleReviewOrder = async () => {
        setIsSubmiting(true);
        if (rating && selectedOrder && orderStore && userStore?.userId) {
          try {
            const feedback: IOrderFeedback = {
              order_id: selectedOrder.order_id,
              buyer_id: selectedOrder.buyer_id,
              seller_id: selectedOrder.seller_id,
            };
            if (selectedOrder.buyer_id === userStore.userId) {
              feedback.buyer_rating = rating;
              feedback.buyer_comment = comment ?? "";
            } else {
              feedback.seller_rating = rating;
              feedback.seller_comment = comment ?? "";
            }
            await orderStore.giveOrderFeedback(feedback);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } catch (error: any) {
            enqueueSnackbar("Failed to review the order: " + error.message, {
              variant: "error",
            });
          }
          setOpenDialog(false);
        } else {
          setError("Please select a rating to review.");
        }
        setIsSubmiting(false);
      };

      useEffect(() => {
        if (orderStore && userStore?.userId && selectedOrder?.order_id) {
          orderStore
            .getOrderFeedback(selectedOrder.order_id)
            .then((feedback) => {
              if (feedback) {
                if (feedback.buyer_id === userStore.userId) {
                  setRating(feedback.buyer_rating ?? null);
                  setCommnet(feedback.buyer_comment ?? "");
                } else {
                  setRating(feedback.seller_rating ?? null);
                  setCommnet(feedback.seller_comment ?? "");
                }
              }
            });
        }
      }, []);

      return (
        <Dialog
          open={openDialog}
          onClose={() => {
            setOpenDialog(false);
          }}
        >
          <DialogTitle>Review order</DialogTitle>
          <DialogContent>
            <StyledRating
              name="rating"
              value={rating}
              onChange={(event, newRating) => {
                setRating(newRating);
                if (newRating) {
                  setError("");
                }
              }}
              IconContainerComponent={IconContainer}
              getLabelText={(value: number) => customIcons[value].label}
              highlightSelectedOnly
            />
            {error && (
              <Typography
                variant="body2"
                fontSize="0.75rem"
                color={theme.colors.errorFontColor}
                lineHeight="1.244375rem"
                mb="0.5rem"
              >
                {error}
              </Typography>
            )}
            <InputGroup sx={{ mb: "0" }}>
              <InputLabel htmlFor="comment">Comment</InputLabel>
              <TextField
                type="text"
                name="comment"
                id="comment"
                multiline={true}
                value={comment}
                onChange={(e) => setCommnet(e.target.value)}
              />
            </InputGroup>
          </DialogContent>
          <DialogActions>
            <Button
              loading={isSubmiting}
              onClick={() => {
                setOpenDialog(false);
              }}
            >
              Cancel
            </Button>
            <Button loading={isSubmiting} onClick={handleReviewOrder}>
              Review
            </Button>
          </DialogActions>
        </Dialog>
      );
    },
  ),
);

export default Review;
